import { useEffect } from 'react';

import { useFloatingPosition } from 'core/hooks/use-floating-position';
import { getLauncherPlacement } from 'core/components/staff-tools-chat/staff-tools-chat';

// Appcues renders its Launchpad (the floating button and the panel it opens) in an open shadow root.
const HOST_SELECTOR = 'appcues-experience-container';
const BUTTON_ID = 'launchpad-button-wrapper';
// Custom properties inherit into the shadow root, so the host carries the spot and this rule applies it.
// It is an adopted stylesheet because Staff's CSP blocks inline <style> elements.
const PLACEMENT_CSS = `
  #launchpad-button-wrapper {
    top: auto !important;
    left: var(--launcher-left) !important;
    right: var(--launcher-right) !important;
    bottom: var(--launcher-bottom) !important;
    transform: none !important;
  }
  #launchpad-container {
    top: var(--window-top) !important;
    left: var(--window-left) !important;
    right: var(--window-right) !important;
    bottom: var(--window-bottom) !important;
    max-height: var(--window-max-height) !important;
    margin: 0 !important;
    transform: none !important;
  }
`;

let placementSheet;
const getPlacementSheet = () => {
  if (!placementSheet) {
    placementSheet = new CSSStyleSheet();
    placementSheet.replaceSync(PLACEMENT_CSS);
  }
  return placementSheet;
};

// The custom properties getLauncherPlacement sets: the only ones removed from the host, which Appcues owns.
const getPlacementProperties = () => Object.keys(getLauncherPlacement({ right: 0, bottom: 0, width: 0, height: 0 }));

const findHosts = () => [...document.querySelectorAll(HOST_SELECTOR)].filter((host) => host.shadowRoot);
const isPlaced = (host) => host.shadowRoot.adoptedStyleSheets.includes(getPlacementSheet());

const applyPlacement = (host, placement) => {
  const root = host.shadowRoot;
  const sheet = getPlacementSheet();
  root.adoptedStyleSheets = root.adoptedStyleSheets.filter((adopted) => adopted !== sheet);
  getPlacementProperties().forEach((name) => host.style.removeProperty(name));
  if (!placement) {
    return;
  }
  Object.entries(placement).forEach(([name, value]) => host.style.setProperty(name, value));
  root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
};

// Moving the launchpad is cosmetic, so a browser without constructed stylesheets leaves it in Appcues'
// corner instead of throwing from an effect at the app root.
const placeHosts = (placement, { onlyChanged = false } = {}) => {
  try {
    findHosts()
      .filter((host) => !onlyChanged || isPlaced(host) !== !!placement)
      .forEach((host) => applyPlacement(host, placement));
  } catch {
    // The launchpad keeps its default spot.
  }
};

/**
 * Lets staff drag the Appcues Launchpad button out of the way, like the help speed dial and the Staff Tools
 * Assist launcher (#36893): the same saved spot, and "Move buttons back" resets it. Renders nothing.
 */
const AppcuesLaunchpadDrag = () => {
  const { position, startDrag } = useFloatingPosition('appcuesLaunchpad');

  useEffect(() => {
    const placement = getLauncherPlacement(position);
    placeHosts(placement);
    // Appcues mounts the launchpad after the page loads, and may remount it on navigation.
    const observer = new MutationObserver(() => placeHosts(placement, { onlyChanged: true }));
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, [position]);

  useEffect(() => {
    // Events from inside the shadow root reach the document retargeted to the host, so find the button in the path.
    const handlePointerDown = (event) => {
      const path = event.composedPath();
      const button = path.find((element) => element.id === BUTTON_ID);
      if (button) {
        // Capture on the element pressed, not the host: a click goes to the capture target, and Appcues
        // listens on its own button.
        const { button: pointerButton, clientX, clientY, pointerId } = event;
        startDrag({ button: pointerButton, clientX, clientY, pointerId, target: path[0] }, button);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [startDrag]);

  return null;
};

export default AppcuesLaunchpadDrag;
